import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import createHistory from 'history/createBrowserHistory';
import { action, computed, observable } from 'mobx';
import { loadTheme } from 'office-ui-fabric-react/lib/Styling';
import GoogleAnalytics from 'react-ga';
import { get } from 'services/request';
import uuid from 'uuid/v4';
import { prepareTenantConfig } from '../../../helpers/prepareConfig';

const STATE_INIT = 'initializing';
const STATE_READY = 'ready';
const STATE_ERROR = 'error';

const gaOptions = {
  storeGac: false,
  clientId: uuid(),
  storage: 'none',
};
const gaSettings = {
  anonymizeIp: true,
};

const { theme } = require(`../../../@${process.env.REACT_APP_TENANT}/theme.js`);

class AppStore {
  @observable state = STATE_INIT; // "initializing" | "ready" | "error"
  @observable config;
  @observable theme;
  @observable data;

  history = createHistory();

  @computed get ready() {
    return this.state === STATE_READY;
  }

  @computed get tenant() {
    return this.config.tenant;
  }

  @computed get currency() {
    return this.tenantConfig.currency;
  }

  @computed get tenantConfig() {
    return prepareTenantConfig(this.config, this.data);
  }

  @computed get features() {
    return {
      ...this.config.features,
      ...(!!this.data ? this.data.features : null),
    };
  }

  @computed get canMls() {
    return (
      this.tenantConfig.mlsTypes &&
      this.tenantConfig.mlsTypes.length >= 1 &&
      (this.tenantConfig.mlsTypes.length > 1 ||
        this.tenantConfig.mlsTypes[0] !== 'none') &&
      this.features &&
      this.features.mls
    );
  }

  constructor() {
    this.theme = theme;
    this.history.listen((location) => {
      GoogleAnalytics.set({
        page: location.pathname + location.search,
        ...gaSettings,
      });
      GoogleAnalytics.pageview(location.pathname + location.search);
    });
  }

  @action setTheme(theme) {
    this.theme = theme;
    loadTheme({ ...theme });
  }

  @action setData(data) {
    this.data = { ...data };
  }

  @action init() {
    get('/.well-known/configuration/dashboard').then(
      this.initSuccess,
      this.initError
    );
  }

  @action.bound initSuccess(config) {
    this.state = STATE_READY;
    this.config = config;

    if (!config || !config.client) {
      return;
    }

    if (!this.tenant.startsWith('propnow')) {
      loadTheme(theme);
    }

    if (config.client.ga) {
      GoogleAnalytics.initialize(config.client.ga, { gaOptions });
      GoogleAnalytics.set({
        page: window.location.pathname + window.location.search,
        ...gaSettings,
      });
      GoogleAnalytics.pageview(
        window.location.pathname + window.location.search
      );
    }

    if (config.client.appInsights) {
      var reactPlugin = new ReactPlugin();
      var appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: config.client.appInsights,
          extensions: [reactPlugin],
          extensionConfig: {
            [reactPlugin.identifier]: { history: this.history },
          },
        },
      });
      appInsights.loadAppInsights();
    }
  }

  @action.bound initError(error) {
    this.state = STATE_ERROR;
  }

  getSaleFeatures(additional) {
    return Object.fromEntries(
      [
        ['disableRightOfWithdrawal', this.features.disableRightOfWithdrawal],
        ['alwaysShowBuyerConditions', this.features.alwaysShowBuyerConditions],
        ['neverShowBuyerConditions', this.features.neverShowBuyerConditions],
        ['bidNotificationsToAll', this.features.bidNotificationsToAll],
        ['hideBiddingHistoryOnClose', this.features.hideBiddingHistoryOnClose],
        ['disableAutomaticReminders', this.features.disableAutomaticReminders],
        ...Object.entries(additional || {}),
      ].filter((a) => !!a[1])
    );
  }
}

const app = new AppStore();

export default app;
export { AppStore };



// WEBPACK FOOTER //
// ./src/app/containers/App/stores/AppStore.js